import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { catchError, map, tap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import {
  DEFAULT_AI_SNAPSHOT_MODEL,
  DEFAULT_AI_SNAPSHOT_PROMPT
} from '../constants/ai-snapshot-prompt';
import 'rxjs/add/observable/throw';

export interface AiSnapshotSettingsDto {
  prompt: string;
  model: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface AiSnapshotSettingsState {
  prompt: string;
  model: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
  savedToDatabase: boolean;
}

export interface AiSnapshotActiveConfig {
  prompt: string;
  model: string;
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class AiSettingsService {
  private readonly apiUrl = environment.apiUrl.replace(/\/$/, '');
  private cachedActiveConfig: AiSnapshotActiveConfig | null = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.authService.isAuthorization.subscribe(isAuthorized => {
      if (!isAuthorized) {
        this.clearCache();
      }
    });
  }

  getAiSnapshotSettings(): Observable<AiSnapshotSettingsState> {
    return this.http
      .get<AiSnapshotSettingsDto>(this.authUrl('/admin/ai-snapshot-settings'), httpOptions)
      .pipe(
        map(dto => this.mapDtoToState(dto)),
        catchError(err => this.handleAdminReadError(err))
      );
  }

  getActiveConfig(): Observable<AiSnapshotActiveConfig> {
    const token = this.authService.getToken();
    if (!token) {
      return of(this.getDefaultActiveConfig());
    }

    if (this.cachedActiveConfig) {
      return of(this.cachedActiveConfig);
    }

    return this.http
      .get<AiSnapshotSettingsDto>(this.authUrl('/ai-snapshot-settings/active'), httpOptions)
      .pipe(
        map(dto => this.mapDtoToActiveConfig(dto)),
        tap(config => {
          this.cachedActiveConfig = config;
        }),
        catchError(err => {
          if (err.status === 401) {
            return Observable.throw(new Error('Authorization required'));
          }

          const defaults = this.getDefaultActiveConfig();
          this.cachedActiveConfig = defaults;
          return of(defaults);
        })
      );
  }

  saveAiSnapshotSettings(prompt: string, model: string): Observable<AiSnapshotSettingsDto> {
    const token = this.authService.getToken();
    if (!token) {
      return Observable.throw(new Error('No authentication token available. Please log in.'));
    }

    return this.http
      .patch<AiSnapshotSettingsDto>(
        this.authUrl('/admin/ai-snapshot-settings'),
        { prompt, model },
        httpOptions
      )
      .pipe(
        tap(dto => {
          this.cachedActiveConfig = this.mapDtoToActiveConfig(dto);
        })
      );
  }

  clearCache(): void {
    this.cachedActiveConfig = null;
  }

  parseApiError(error: any, fallback: string): string {
    const apiError = error && error.error && error.error.error;
    if (apiError && apiError.message) {
      return apiError.message;
    }

    if (error && error.message) {
      return error.message;
    }

    return fallback;
  }

  private handleAdminReadError(err: any): Observable<AiSnapshotSettingsState> {
    if (err.status === 403 || err.status === 401) {
      return Observable.throw(err);
    }

    return of(this.getDefaultState());
  }

  private authUrl(path: string): string {
    const token = this.authService.getToken();
    if (!token) {
      throw new Error('No authentication token available. Please log in.');
    }

    const normalizedPath = path.indexOf('/') === 0 ? path : `/${path}`;
    return `${this.apiUrl}${normalizedPath}?access_token=${encodeURIComponent(token)}`;
  }

  private mapDtoToState(dto: AiSnapshotSettingsDto): AiSnapshotSettingsState {
    return {
      prompt: (dto && dto.prompt) || DEFAULT_AI_SNAPSHOT_PROMPT,
      model: (dto && dto.model) || DEFAULT_AI_SNAPSHOT_MODEL,
      updatedAt: dto ? dto.updatedAt : null,
      updatedBy: dto ? dto.updatedBy : null,
      savedToDatabase: !!(dto && dto.updatedAt)
    };
  }

  private mapDtoToActiveConfig(dto: AiSnapshotSettingsDto): AiSnapshotActiveConfig {
    return {
      prompt: (dto && dto.prompt) || DEFAULT_AI_SNAPSHOT_PROMPT,
      model: (dto && dto.model) || DEFAULT_AI_SNAPSHOT_MODEL
    };
  }

  private getDefaultState(): AiSnapshotSettingsState {
    return {
      prompt: DEFAULT_AI_SNAPSHOT_PROMPT,
      model: DEFAULT_AI_SNAPSHOT_MODEL,
      savedToDatabase: false
    };
  }

  private getDefaultActiveConfig(): AiSnapshotActiveConfig {
    return {
      prompt: DEFAULT_AI_SNAPSHOT_PROMPT,
      model: DEFAULT_AI_SNAPSHOT_MODEL
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/services/ai-settings.service.ts